/// <reference types="vite-plugin-svgr/client" />
import { RoundedLogo } from '@assets/newlogo';
import { ButtonLabel } from '@moreharvest/component-library';
import DesktopAuthWrapper from '@components/atoms/DesktopAuthWrapper';
import { useMutationState } from '@tanstack/react-query';
import { usePasswordless } from '@hooks/api';
import { login, reauth } from '@services/analytics';
import { AnalyticsAuthMethod, AnalyticsAuthStage } from 'mh-client';
import { useLoadingModal } from '@hooks/useLoadingModal';
import { useRedirect } from '@hooks/useRedirect';
import { useTitle } from '@hooks/useTitle';
import Oops from '@pages/error/Oops';
import { useAppDispatch, useAppSelector } from '@store/index';
import { selectEmail, setProfileAuthInfo } from '@store/reducers/profile';
import { FormattedMessage } from '@utils/i18n';
import { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';

//TODO: create a wrapper component for the logo that handles diffrent sizes
const buildURL = (setting?: string, action?: string, id?: string): string => {
    let url = '';
    if (setting) url += `${setting}/`;
    if (action) url += `${action}/`;
    if (id) url += encodeURIComponent(id);

    return url;
};

const SendMagicLink = () => {
    useTitle('send_magic_link', 'magicLink');
    const email = useAppSelector(selectEmail);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { setting, action, id } = useParams();
    const url = buildURL(setting, action, id);
    const fireAuth = url ? reauth : login;
    const initialForce = state?.force ?? false;
    const hasSentRef = useRef(false);

    useRedirect(!!email, '/login');

    // Pass onSuccess to hook (hook-level callbacks work, component-level don't)
    const { mutate } = usePasswordless({
        onSuccess: data => {
            const methods = data.methods.asJsReadonlyArrayView();

            if (methods.length > 0) {
                const storedMethods = methods
                    .filter(m => m.name !== 'magicLink')
                    .map(m => m.name);
                dispatch(
                    setProfileAuthInfo({
                        email: email,
                        methods: storedMethods,
                    })
                );

                if (methods.some(m => m.name === 'webauthn')) {
                    navigate('/login/passkey', { replace: true });
                } else if (methods.some(m => m.name === 'password')) {
                    navigate('/login/password', { replace: true });
                }
            }
        },
    });

    // Read mutation state directly from cache (useMutation's isPending is broken)
    const mutationStates = useMutationState({
        filters: { mutationKey: ['passwordless'] },
        select: mutation => mutation.state,
    });
    const latestMutation = mutationStates[mutationStates.length - 1];
    const isPending = latestMutation?.status === 'pending';
    const isError = latestMutation?.status === 'error';

    useEffect(() => {
        if (email && !hasSentRef.current) {
            hasSentRef.current = true;
            mutate({
                email,
                url: 'login/magic-link/' + url,
                force: initialForce,
            });
        }
    }, [email, url, initialForce, mutate]);

    const resendMagicLink = () => {
        fireAuth({
            method: AnalyticsAuthMethod.MagicLink,
            stage: AnalyticsAuthStage.Resend,
        });
        mutate({
            email,
            url: 'login/magic-link/' + url,
            force: true,
        });
    };

    const handleBack = () => {
        let backMethod: AnalyticsAuthMethod = AnalyticsAuthMethod.Email;
        if (url) {
            backMethod =
                setting === 'profile'
                    ? AnalyticsAuthMethod.PayoutSetting
                    : AnalyticsAuthMethod.SecurityConfig;
        }
        fireAuth({ method: backMethod, stage: AnalyticsAuthStage.Back });
    };

    useLoadingModal(isPending);

    if (isError) {
        return <Oops />;
    }

    return (
        <DesktopAuthWrapper>
            <div className="flex flex-col items-center gap-y-12">
                <RoundedLogo size="small" />
                <p className="Body-Medium text-center">
                    <FormattedMessage
                        id="login_magic_link_info"
                        values={{
                            0: email,
                        }}
                    />
                </p>
                <div className="flex flex-col gap-y-4">
                    <Link
                        to={url ? '/security' : '/'}
                        id="magiclink_link_login"
                        onClick={handleBack}
                    >
                        <ButtonLabel
                            variant="solid"
                            type="button"
                            size="medium"
                        >
                            {url ? (
                                <FormattedMessage id="back_to_security_settings" />
                            ) : (
                                <FormattedMessage id="back_to_log_in" />
                            )}
                        </ButtonLabel>
                    </Link>
                    <ButtonLabel
                        variant="empty"
                        type="button"
                        size="small"
                        onClick={() => resendMagicLink()}
                        id="magiclink_CTA_resendMagiclink"
                    >
                        <FormattedMessage id="security_magic_link_click_here_to_resend" />
                    </ButtonLabel>
                </div>
            </div>
        </DesktopAuthWrapper>
    );
};

export default SendMagicLink;
